/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * ORELLA ENCOUNTER DEFINITION — v119.4
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * THE THREE-PART ENCOUNTER:
 * Every appointment in Orella is defined by exactly three properties:
 *
 *   1. CATEGORY  (purpose)  — WHY the encounter exists
 *   2. MODALITY  (format)   — HOW the encounter is conducted
 *   3. STATUS    (state)    — WHERE the encounter is in its lifecycle
 *
 * Together, these three values deterministically select:
 *   → Which database columns get written
 *   → Which UX screens/states appear for the patient
 *   → Which UX sections/workflows appear for the doctor
 *   → Which scheduling modal options to show
 *   → Which Deliver screen layout to render
 *   → Which notifications to send
 *
 * CANONICAL COPY: This file is the source of truth.
 *   Identical copies live in:
 *     - orella-patient-app/src/config/encounterConfig.js
 *     - orella-md-panel/src/config/encounterConfig.js
 *   API-side constants are defined in index.py and MUST mirror these values.
 *
 * RULE: Never use `type` (modality) to infer category. Never use `type` to
 *       decide which DB columns to write. Always use `appointment_category`.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 */

// ─────────────────────────────────────────────────────────────────────────────
// BRAND CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

export const ORELLA_BRAND = {
  navy: '#15354F',
  blue: '#46A8F9',
  lightBlue: '#A7D9FC',
  iceBlue: '#E9F8FF',
  green: '#66D170',
  lightGreen: '#D5EFD3',
}

export const ZOOM_BLUE = '#0B5CFF'

// ─────────────────────────────────────────────────────────────────────────────
// DIMENSION 1: CATEGORY (purpose — WHY)
//
// This is the MOST IMPORTANT dimension. It determines:
//   → Which case data columns are read/written
//   → Which UX workflow the patient/doctor enters
//   → Which section of the case is affected
//
// CRITICAL: `type` field on appointments is MODALITY (video/in_person),
//           NOT category. Never use `type` to determine category.
// ─────────────────────────────────────────────────────────────────────────────

export const ENCOUNTER_CATEGORIES = {
  initial: {
    id: 'initial',
    apiValue: 'initial',           // Value stored in appointments.appointment_category
    label: 'Initial Consult',
    shortLabel: 'Initial',
    description: 'First visit for this concern',
    // ── Visual ──
    color: '#46A8F9',              // Orella brand blue
    light: '#E9F8FF',
    border: '#A7D9FC',
    text: '#15354F',
    lucideIcon: 'Stethoscope',
    // ── Rules ──
    modalities: ['in_person', 'video'],
    defaultModality: 'in_person',
    allowedInitiators: ['patient', 'doctor'],
    // ── Data routing ──
    // These define which DB columns this category reads and writes.
    // This is the ISOLATION contract — categories NEVER cross-write.
    dataRouting: {
      writes: ['appointment_id', 'appointment_request'],    // Case columns written
      neverWrites: ['follow_up'],                           // Case columns NEVER touched
      appointmentTable: true,                               // Creates row in appointments table
      readsFrom: ['appointment', 'appointmentRequest'],     // Frontend fields to read
    },
    // ── UX pathway ──
    patientUX: {
      schedulerScreen: '/case/schedule',                    // Patient scheduling screen
      deliverState: null,                                   // No deliver state (initial)
      showsInVisits: true,                                  // Appears in "My Visits"
    },
    doctorUX: {
      workflowSection: 'DiagnoseWorkflowSection',           // MD panel workflow component
      schedulerType: 'initial',                             // Schedule modal config
      caseStatusOnSchedule: 'in_progress',                  // Case status after scheduling
    },
  },

  followUp: {
    id: 'followUp',
    apiValue: 'follow_up',
    label: 'Follow-up',
    shortLabel: 'Follow-up',
    description: 'Return visit for ongoing care',
    color: '#66D170',
    light: '#F0FDF4',
    border: '#BBF7D0',
    text: '#166534',
    lucideIcon: 'RefreshCw',
    modalities: ['in_person', 'video'],
    defaultModality: 'in_person',
    allowedInitiators: ['patient', 'doctor'],
    dataRouting: {
      writes: ['follow_up'],                                // ONLY follow_up column
      neverWrites: ['appointment_id', 'appointment_request'], // NEVER touch initial appt data
      appointmentTable: true,
      readsFrom: ['followUpAppointment', 'followUp'],
    },
    patientUX: {
      schedulerScreen: '/case/schedule-followup',
      deliverState: 'follow-up',                            // DeliverScreen state
      showsInVisits: true,
    },
    doctorUX: {
      workflowSection: 'FollowUpWorkflowSection',
      schedulerType: 'follow-up',
      caseStatusOnSchedule: null,                           // Don't change case status
    },
  },

  referral: {
    id: 'referral',
    apiValue: 'referral',
    label: 'Referral',
    shortLabel: 'Referral',
    description: 'Case referred to specialist',
    color: '#15354F',
    light: '#E9F8FF',
    border: '#A7D9FC',
    text: '#15354F',
    lucideIcon: 'ArrowDownLeft',
    modalities: ['in_person', 'video'],
    defaultModality: 'in_person',
    allowedInitiators: ['patient', 'doctor'],
    dataRouting: {
      writes: ['follow_up'],                                // Referral data lives in follow_up
      neverWrites: ['appointment_id', 'appointment_request'],
      appointmentTable: true,
      readsFrom: ['followUpAppointment', 'followUp'],
    },
    patientUX: {
      schedulerScreen: null,                                // Referral = patient contacts specialist
      deliverState: 'referral',
      showsInVisits: true,
    },
    doctorUX: {
      workflowSection: 'FollowUpWorkflowSection',
      schedulerType: 'follow-up',
      caseStatusOnSchedule: null,
    },
  },

  procedure: {
    id: 'procedure',
    apiValue: 'procedure',
    label: 'Procedure',
    shortLabel: 'Procedure',
    description: 'In-office procedure',
    // v221.1.4: conformed to canonical Procedure Purple (per v221.0 canon)
    color: '#7B5BC9',
    light: '#F1ECFA',
    border: '#D9CCF1',
    text: '#5C3FA3',
    lucideIcon: 'Syringe',
    modalities: ['in_person'],                              // In-person only
    defaultModality: 'in_person',
    allowedInitiators: ['doctor'],                           // MD-initiated only
    dataRouting: {
      writes: ['follow_up'],
      neverWrites: ['appointment_id', 'appointment_request'],
      appointmentTable: true,
      readsFrom: ['followUpAppointment', 'followUp'],
    },
    patientUX: {
      schedulerScreen: null,                                // Doctor schedules procedures
      deliverState: 'follow-up',
      showsInVisits: true,
    },
    doctorUX: {
      workflowSection: 'FollowUpWorkflowSection',
      schedulerType: 'follow-up',
      caseStatusOnSchedule: null,
    },
  },

  surgery: {
    id: 'surgery',
    apiValue: 'surgery',
    label: 'Surgery',
    shortLabel: 'Surgery',
    description: 'Surgical procedure',
    color: '#64748B',
    light: '#F1F5F9',
    border: '#CBD5E1',
    text: '#475569',
    lucideIcon: 'Syringe',
    modalities: ['in_person'],
    defaultModality: 'in_person',
    allowedInitiators: ['doctor'],
    dataRouting: {
      writes: ['follow_up'],
      neverWrites: ['appointment_id', 'appointment_request'],
      appointmentTable: true,
      readsFrom: ['followUpAppointment', 'followUp'],
    },
    patientUX: {
      schedulerScreen: null,
      deliverState: 'follow-up',
      showsInVisits: true,
    },
    doctorUX: {
      workflowSection: 'FollowUpWorkflowSection',
      schedulerType: 'follow-up',
      caseStatusOnSchedule: null,
    },
  },
}

// ─────────────────────────────────────────────────────────────────────────────
// DIMENSION 2: MODALITY (format — HOW)
//
// Determines the physical/virtual format of the encounter.
// This is stored in appointments.type (NOT appointment_category).
//
// CRITICAL: Modality is NEVER used to determine category.
//           A video follow-up is still category=follow_up, type=video.
// ─────────────────────────────────────────────────────────────────────────────

export const ENCOUNTER_MODALITIES = {
  in_person: {
    id: 'in_person',
    label: 'In-Person',
    shortLabel: 'In-Person',
    lucideIcon: 'Building',
    // ── UX implications ──
    showsOfficeLocation: true,        // Show practice name, address, phone
    showsDirectionsButton: true,      // "Get Directions" action
    showsVideoCallButton: false,      // No "Join Video Call"
    requiresZoomLink: false,
  },
  video: {
    id: 'video',
    label: 'Virtual Visit',
    shortLabel: 'Virtual',
    lucideIcon: 'Video',
    showsOfficeLocation: false,       // Practice info still shown, but not as navigation
    showsDirectionsButton: false,
    showsVideoCallButton: true,       // "Join Video Call" button
    requiresZoomLink: true,           // Auto-generate Zoom meeting on schedule
  },
}

// Legacy modality aliases → canonical value
export const MODALITY_ALIASES = {
  'in_person': 'in_person',
  'in-person': 'in_person',
  'inPerson': 'in_person',
  'office': 'in_person',
  'video': 'video',
  'telehealth': 'video',
  'virtual': 'video',
  'telemedicine': 'video',
}

export function normalizeModality(raw) {
  if (!raw) return 'in_person'
  return MODALITY_ALIASES[raw] || MODALITY_ALIASES[raw.toLowerCase()] || 'in_person'
}

// ─────────────────────────────────────────────────────────────────────────────
// DIMENSION 3: STATUS (lifecycle state — WHERE)
//
// Determines the appointment's position in its lifecycle.
// Drives card colors, available actions, and notification triggers.
// ─────────────────────────────────────────────────────────────────────────────

export const ENCOUNTER_STATUSES = {
  // ── Pre-scheduled ──
  draft: {
    id: 'draft',
    label: 'Draft',
    colorFamily: 'gray',
    actions: ['schedule', 'cancel'],
  },
  requested: {
    id: 'requested',
    label: 'Requested',
    colorFamily: 'amber',            // Amber = needs attention
    actions: ['confirm', 'reschedule', 'decline'],
  },
  pending: {
    id: 'pending',
    label: 'Pending',
    colorFamily: 'amber',
    actions: ['confirm', 'reschedule', 'decline'],
  },
  // ── Scheduled ──
  scheduled: {
    id: 'scheduled',
    label: 'Confirmed',
    colorFamily: 'category',          // Uses category color (blue/green/teal/etc)
    actions: ['reschedule', 'cancel', 'start'],
  },
  confirmed: {
    id: 'confirmed',
    label: 'Confirmed',
    colorFamily: 'category',
    actions: ['reschedule', 'cancel', 'start'],
  },
  rescheduled: {
    id: 'rescheduled',
    label: 'Rescheduled',
    colorFamily: 'amber',
    actions: ['confirm', 'reschedule', 'cancel'],
  },
  // ── Active ──
  in_progress: {
    id: 'in_progress',
    label: 'In Progress',
    colorFamily: 'category',
    actions: ['complete'],
  },
  // ── Terminal ──
  completed: {
    id: 'completed',
    label: 'Completed',
    colorFamily: 'gray',
    actions: ['reopen'],
  },
  cancelled: {
    id: 'cancelled',
    label: 'Cancelled',
    colorFamily: 'muted',
    actions: ['reschedule'],
  },
  declined: {
    id: 'declined',
    label: 'Declined',
    colorFamily: 'muted',
    actions: ['reschedule'],
  },
}

// ★ PROD-921: a status the API writes that renders as another one. 'pending_reschedule' (a patient
//   reschedule of a confirmed visit, physician in manual_review) is a pending request until the
//   physician approves the new time. Before, it was an unknown status and getCardColor drew it in
//   the confirmed category colour. Import-free mirror of utils/pendingReschedule.js asPendingRequest
//   (check-pending-reschedule PR-0 holds the two equal).
export const STATUS_ALIASES = {
  pending_reschedule: 'pending',
}

// ─────────────────────────────────────────────────────────────────────────────
// STATUS COLOR FAMILIES
// ─────────────────────────────────────────────────────────────────────────────

export const STATUS_COLORS = {
  amber: {
    primary: '#F59E0B',
    light: '#FFFBEB',
    border: '#FDE68A',
    text: '#B45309',
    statusIcon: 'Clock',
  },
  gray: {
    primary: '#94A3B8',
    light: '#F8FAFC',
    border: '#E2E8F0',
    text: '#64748B',
    statusIcon: 'Check',
  },
  muted: {
    primary: '#CBD5E1',
    light: '#F8FAFC',
    border: '#E2E8F0',
    text: '#94A3B8',
    statusIcon: 'X',
  },
}

// Backwards-compatible exports
export const STATUS_AMBER = STATUS_COLORS.amber
export const STATUS_GRAY = STATUS_COLORS.gray
export const STATUS_CANCELED = STATUS_COLORS.muted

// ─────────────────────────────────────────────────────────────────────────────
// CARD COLOR RESOLVER
//
// Given status + category → returns the ONE color family for the entire card.
//   colorFamily='amber'    → Amber (needs attention)
//   colorFamily='category' → Category color (blue/green/teal/etc)
//   colorFamily='gray'     → Muted gray (terminal)
// ─────────────────────────────────────────────────────────────────────────────

export function getCardColor(status, category) {
  const cat = ENCOUNTER_CATEGORIES[category] || ENCOUNTER_CATEGORIES.initial
  const statusDef = ENCOUNTER_STATUSES[STATUS_ALIASES[status] || status]

  if (!statusDef) {
    // Unknown status → treat as confirmed in category color
    return {
      label: status || 'Scheduled',
      primary: cat.color,
      light: cat.light,
      border: cat.border,
      text: cat.text,
      statusIcon: 'Check',
    }
  }

  if (statusDef.colorFamily === 'category') {
    return {
      label: statusDef.label,
      primary: cat.color,
      light: cat.light,
      border: cat.border,
      text: cat.text,
      statusIcon: statusDef.id === 'in_progress' ? 'Play' : 'Check',
    }
  }

  const colors = STATUS_COLORS[statusDef.colorFamily] || STATUS_COLORS.gray
  return {
    label: statusDef.label,
    ...colors,
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// DATA ROUTING HELPERS
//
// These functions enforce the isolation contract.
// Use them instead of ad-hoc if/else chains.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Given an appointment_category, returns which case columns this category
 * is allowed to write.
 */
export function getWritableColumns(category) {
  const cat = ENCOUNTER_CATEGORIES[category] || ENCOUNTER_CATEGORIES[normalizeCategory(category)]
  return cat?.dataRouting?.writes || ['follow_up']
}

/**
 * Given an appointment_category, returns which case columns this category
 * must NEVER touch.
 */
export function getForbiddenColumns(category) {
  const cat = ENCOUNTER_CATEGORIES[category] || ENCOUNTER_CATEGORIES[normalizeCategory(category)]
  return cat?.dataRouting?.neverWrites || []
}

/**
 * Returns true if this category represents an initial encounter.
 * Used to guard appointment_id and appointment_request writes.
 */
export function isInitialCategory(category) {
  const normalized = normalizeCategory(category)
  return normalized === 'initial'
}

/**
 * Returns true if this category represents a non-initial encounter
 * (follow-up, referral, procedure, surgery).
 * Used to guard follow_up column writes.
 */
export function isSubsequentCategory(category) {
  return !isInitialCategory(category)
}

// ─────────────────────────────────────────────────────────────────────────────
// UTILITY FUNCTIONS
// ─────────────────────────────────────────────────────────────────────────────

export function categoryAllowsVideo(category) {
  const cat = ENCOUNTER_CATEGORIES[category]
  return cat ? cat.modalities.includes('video') : false
}

export function categoryIsDoctorOnly(category) {
  const cat = ENCOUNTER_CATEGORIES[category]
  return cat ? !cat.allowedInitiators.includes('patient') : false
}

export function getCategoriesForRole(role) {
  return Object.values(ENCOUNTER_CATEGORIES).filter(cat =>
    cat.allowedInitiators.includes(role)
  )
}

/**
 * Normalize appointment_category values from API.
 * Handles legacy values like 'follow_up', 'follow-up', 'observation_checkin', etc.
 */
export function normalizeCategory(raw) {
  if (!raw) return 'initial'
  if (ENCOUNTER_CATEGORIES[raw]) return raw

  const normalized = raw.toLowerCase().replace(/-/g, '_')
  const LEGACY_MAP = {
    'follow_up': 'followUp',
    'follow-up': 'followUp',
    'followup': 'followUp',
    'observation_checkin': 'followUp',
    'reopened': 'followUp',
    'initial_consult': 'initial',
    'virtual': 'initial',
  }
  return LEGACY_MAP[normalized] || 'initial'
}

/**
 * Determine default category from case context.
 */
export function inferCategoryFromCase(caseData) {
  if (!caseData) return 'initial'

  const followUp = caseData.follow_up || caseData.followUp
  const fu = typeof followUp === 'string' ? JSON.parse(followUp || '{}') : (followUp || {})
  const fuType = fu.type || ''

  if (fuType === 'in_follow_up' || fuType === 'followup') return 'followUp'
  if (fuType === 'referral') return 'followUp' // v120.9.1: referral is add-on, not disposition — legacy fallback
  if (fuType === 'observation') return 'followUp'

  const status = caseData.status || ''
  if (status === 'in_follow_up' || status === 'completed') return 'followUp'

  return 'initial'
}

// ─────────────────────────────────────────────────────────────────────────────
// API CATEGORY MAPS
// ─────────────────────────────────────────────────────────────────────────────

// Frontend camelCase → API snake_case
export const API_CATEGORY_MAP = {
  'initial': 'initial',
  'followUp': 'follow_up',
  'follow_up': 'follow_up',
  'referral': 'referral',
  'procedure': 'procedure',
  'surgery': 'surgery',
}

// API snake_case → Frontend camelCase
export const FROM_API_CATEGORY_MAP = {
  'initial': 'initial',
  'follow_up': 'followUp',
  'referral': 'referral',
  'procedure': 'procedure',
  'surgery': 'surgery',
  'observation_checkin': 'followUp',
  'reopened': 'followUp',
}

export const VALID_APPOINTMENT_CATEGORIES = Object.keys(ENCOUNTER_CATEGORIES)

// ─────────────────────────────────────────────────────────────────────────────
// BACKWARDS COMPATIBILITY
// ─────────────────────────────────────────────────────────────────────────────

export const ENCOUNTER_COLORS = Object.fromEntries(
  Object.entries(ENCOUNTER_CATEGORIES).map(([key, cat]) => [
    key,
    { primary: cat.color, light: cat.light, border: cat.border, text: cat.text }
  ])
)

// ─────────────────────────────────────────────────────────────────────────────
// ENCOUNTER PHASES (v119.13)
// Each encounter cycles through these phases. The case tracks current_encounter
// and encounter_phase to determine which 4D sections are active.
// ─────────────────────────────────────────────────────────────────────────────

export const ENCOUNTER_PHASES = {
  // Encounter 1 (Initial) phases
  describe:              'describe',
  detect:                'detect',
  diagnose_pending:      'diagnose_pending',
  diagnose_active:       'diagnose_active',
  diagnose_acknowledged: 'diagnose_acknowledged',
  deliver_active:        'deliver_active',

  // Encounter 2+ additional phases
  deliver_submitted:     'deliver_submitted',

  // Terminal
  complete:              'complete',
}

// Phase → 4D section states for CaseHome grid
export const PHASE_SECTION_STATES = {
  // Encounter 1 linear flow
  describe:              { describe: 'active',   detect: 'locked',   diagnose: 'locked',   deliver: 'locked' },
  detect:                { describe: 'complete',  detect: 'active',   diagnose: 'locked',   deliver: 'locked' },
  diagnose_pending:      { describe: 'complete',  detect: 'complete', diagnose: 'pending',  deliver: 'locked' },
  diagnose_active:       { describe: 'complete',  detect: 'complete', diagnose: 'ready',    deliver: 'locked' },
  diagnose_acknowledged: { describe: 'complete',  detect: 'complete', diagnose: 'complete', deliver: 'active' },
  deliver_active:        { describe: 'complete',  detect: 'complete', diagnose: 'complete', deliver: 'active' },

  // Encounter 2+ cycle phases
  deliver_submitted:     { describe: 'review',    detect: 'review',   diagnose: 'pending',  deliver: 'submitted' },
  // diagnose_pending with enc >= 2: same as deliver_submitted visually
  // diagnose_active with enc >= 2: { describe: 'review', detect: 'review', diagnose: 'ready', deliver: 'locked' }
  // diagnose_acknowledged with enc >= 2: opens next Deliver

  // Terminal
  complete:              { describe: 'review',    detect: 'review',   diagnose: 'review',   deliver: 'complete' },
}

// Helper: is this a follow-up encounter phase?
export const isFollowUpPhase = (encounterNumber) => (encounterNumber || 1) >= 2

// Helper: get section state for current encounter
export const getSectionState = (encounterPhase, encounterNumber, section) => {
  const enc = encounterNumber || 1
  const states = PHASE_SECTION_STATES[encounterPhase]
  if (!states) return 'locked'

  const base = states[section] || 'locked'

  // Override for encounter 2+ — Describe/Detect are always review-only
  if (enc >= 2 && (section === 'describe' || section === 'detect')) {
    return base === 'active' ? 'review' : base
  }

  return base
}
